/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import ArrowDropUpRoundedIcon from "@mui/icons-material/ArrowDropUpRounded";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import styles from "./../page/ApplicationList/ApplicationList.module.css";
import { getConfig } from "../config";

const STATUS_LABELS = {
  DRAFT: "Draft",
  IN_REVIEW: "In Review",
  PUBLISHED: "Published",
  REJECTED_FOR_PRODUCTION: "Rejected for Production",
  APPROVED_FOR_PRODUCTION: "Approved for Production",
  APPROVED_FOR_BETA: "Approved for Beta",
  SUBMITTED_FOR_STAGING: "Submitted for Staging",
  DELISTED_BY_SELLER: "Delisted by Seller",
  DELISTED_BY_ADMIN: "Delisted by Admin",
  SUBMITTED_FOR_PRODUCTION: "Submitted for Production",
  APPROVED_FOR_STAGING: "Approved for staging",
  DELISTED_BY_APPROVER: "Delisted by approver",
};

const STATUS_ICON_PATHS = {
  DRAFT: [
    "M6.02353 16V12.9129L11.5576 7.41647C11.7082 7.26588 11.8745 7.15294 12.0565 7.07765C12.2384 7.00235 12.4298 6.96471 12.6306 6.96471C12.8314 6.96471 13.0227 7.00235 13.2047 7.07765C13.3867 7.15294 13.5529 7.26588 13.7035 7.41647L14.6259 8.35765C14.7639 8.50824 14.8706 8.67451 14.9459 8.85647C15.0212 9.03843 15.0588 9.22353 15.0588 9.41177C15.0588 9.6 15.018 9.7851 14.9365 9.96706C14.8549 10.149 14.7451 10.3153 14.6071 10.4659L9.11059 16H6.02353ZM12.5929 10.3906L13.5529 9.41177L12.6306 8.47059L11.6518 9.44941L12.5929 10.3906ZM6.58824 6.4L5.53412 5.32706L6.90824 3.95294H4.38588C4.27294 4.27922 4.09726 4.56157 3.85882 4.8C3.62039 5.03843 3.33804 5.21412 3.01176 5.32706V11.6141C3.45098 11.7773 3.81176 12.0502 4.09412 12.4329C4.37647 12.8157 4.51765 13.2518 4.51765 13.7412C4.51765 14.3686 4.29804 14.902 3.85882 15.3412C3.41961 15.7804 2.88627 16 2.25882 16C1.63137 16 1.09804 15.7804 0.658824 15.3412C0.219608 14.902 0 14.3686 0 13.7412C0 13.2518 0.141176 12.8188 0.423529 12.4424C0.705882 12.0659 1.06667 11.7961 1.50588 11.6329V5.32706C1.06667 5.16392 0.705882 4.89098 0.423529 4.50824C0.141176 4.12549 0 3.68941 0 3.2C0 2.57255 0.219608 2.03922 0.658824 1.6C1.09804 1.16078 1.63137 0.941176 2.25882 0.941176C2.74824 0.941176 3.18431 1.08235 3.56706 1.36471C3.9498 1.64706 4.22275 2.00784 4.38588 2.44706H6.90824L5.53412 1.07294L6.58824 0L9.78824 3.2L6.58824 6.4ZM12.8 0.941176C13.4275 0.941176 13.9608 1.16078 14.4 1.6C14.8392 2.03922 15.0588 2.57255 15.0588 3.2C15.0588 3.82745 14.8392 4.36078 14.4 4.8C13.9608 5.23922 13.4275 5.45882 12.8 5.45882C12.1725 5.45882 11.6392 5.23922 11.2 4.8C10.7608 4.36078 10.5412 3.82745 10.5412 3.2C10.5412 2.57255 10.7608 2.03922 11.2 1.6C11.6392 1.16078 12.1725 0.941176 12.8 0.941176Z",
  ],
  IN_REVIEW: [
    "M3.19998 16C2.75998 16 2.38331 15.8433 2.06998 15.53C1.75664 15.2167 1.59998 14.84 1.59998 14.4V1.6C1.59998 1.16 1.75664 0.783333 2.06998 0.47C2.38331 0.156667 2.75998 0 3.19998 0H10.4L14.4 4.8V14.12L10.76 10.4C10.9066 10.1733 11.0166 9.92667 11.09 9.66C11.1633 9.39333 11.2 9.10667 11.2 8.8C11.2 7.92 10.8866 7.16667 10.26 6.54C9.63331 5.91333 8.87998 5.6 7.99998 5.6C7.11998 5.6 6.36664 5.91333 5.73998 6.54C5.11331 7.16667 4.79998 7.92 4.79998 8.8C4.79998 9.68 5.11331 10.4333 5.73998 11.06C6.36664 11.6867 7.11998 12 7.99998 12C8.30664 12 8.59331 11.9633 8.85998 11.89C9.12664 11.8167 9.37331 11.7067 9.59998 11.56L13.76 15.68C13.4533 15.8133 13.1333 15.9 12.8 15.94C12.4666 15.98 12.1333 16 11.8 16H3.19998ZM7.99998 10.4C7.55998 10.4 7.18331 10.2433 6.86998 9.93C6.55664 9.61667 6.39998 9.24 6.39998 8.8C6.39998 8.36 6.55664 7.98333 6.86998 7.67C7.18331 7.35667 7.55998 7.2 7.99998 7.2C8.43998 7.2 8.81664 7.35667 9.12998 7.67C9.44331 7.98333 9.59998 8.36 9.59998 8.8C9.59998 9.24 9.44331 9.61667 9.12998 9.93C8.81664 10.2433 8.43998 10.4 7.99998 10.4Z",
  ],
  PUBLISHED: [
    "M0.2222 6.632L3.62139 3.23281C3.81023 3.04396 4.0328 2.90908 4.28909 2.82814C4.54537 2.74721 4.80841 2.73372 5.07818 2.78768L6.13031 3.01024C5.40192 3.87353 4.82864 4.65588 4.41049 5.3573C3.99233 6.05872 3.58767 6.90852 3.19649 7.90669L0.2222 6.632ZM4.37002 8.47322C4.68026 7.50203 5.10179 6.58479 5.6346 5.7215C6.16741 4.85821 6.8115 4.04888 7.56688 3.29351C8.75389 2.10649 10.1095 1.2196 11.6338 0.632833C13.158 0.0460683 14.5811 -0.132659 15.903 0.0966515C16.1323 1.41856 15.9569 2.84163 15.3769 4.36587C14.7969 5.89011 13.9134 7.24574 12.7264 8.43276C11.9845 9.17464 11.1751 9.81874 10.2984 10.365C9.42159 10.9113 8.49761 11.3396 7.52641 11.6498L4.37002 8.47322ZM9.9544 6.04523C10.2646 6.35548 10.6457 6.5106 11.0976 6.5106C11.5495 6.5106 11.9305 6.35548 12.2408 6.04523C12.551 5.73499 12.7061 5.35393 12.7061 4.90205C12.7061 4.45018 12.551 4.06912 12.2408 3.75887C11.9305 3.44863 11.5495 3.29351 11.0976 3.29351C10.6457 3.29351 10.2646 3.44863 9.9544 3.75887C9.64416 4.06912 9.48904 4.45018 9.48904 4.90205C9.48904 5.35393 9.64416 5.73499 9.9544 6.04523ZM9.38787 15.7774L8.09294 12.8031C9.09111 12.412 9.94429 12.0073 10.6524 11.5891C11.3606 11.171 12.1463 10.5977 13.0096 9.86932L13.212 10.9214C13.2659 11.1912 13.2524 11.4576 13.1715 11.7207C13.0906 11.9837 12.9557 12.2096 12.7668 12.3985L9.38787 15.7774ZM1.51713 11.0631C1.98924 10.591 2.56251 10.3515 3.23696 10.3448C3.9114 10.3381 4.48467 10.5707 4.95678 11.0428C5.42889 11.515 5.66495 12.0882 5.66495 12.7627C5.66495 13.4371 5.42889 14.0104 4.95678 14.4825C4.61956 14.8197 4.0564 15.1097 3.26731 15.3525C2.47821 15.5953 1.38898 15.8112 -0.000366211 16C0.188478 14.6106 0.404299 13.5248 0.647098 12.7424C0.889898 11.9601 1.17991 11.4003 1.51713 11.0631Z",
  ],
  DELISTED_BY_ADMIN: [
    "M3.84575 6.23089C3.37704 5.7677 2.62296 5.7677 2.15425 6.23089L1.63232 6.74668C1.25735 7.11724 0.653522 7.11546 0.280752 6.74269C-0.0920188 6.36992 -0.093805 5.76609 0.276753 5.39112L0.792547 4.86918C1.25574 4.40048 1.25574 3.64639 0.792547 3.17769L0.276753 2.65576C-0.0938049 2.28079 -0.0920189 1.67696 0.280752 1.30419C0.653522 0.931419 1.25735 0.929632 1.63232 1.30019L2.15425 1.81598C2.62296 2.27917 3.37704 2.27917 3.84575 1.81598L4.36768 1.30019C4.74265 0.929633 5.34648 0.931419 5.71925 1.30419C6.09202 1.67696 6.09381 2.28079 5.72325 2.65576L5.20745 3.17769C4.74426 3.64639 4.74426 4.40048 5.20745 4.86918L5.72325 5.39112C6.0938 5.76609 6.09202 6.36991 5.71925 6.74269C5.34648 7.11546 4.74265 7.11724 4.36768 6.74668L3.84575 6.23089Z",
    "M11.4062 7.11523L8.97949 14.9766H6.40234L10.0596 5.02344H11.6934L11.4062 7.11523ZM13.416 14.9766L10.9824 7.11523L10.668 5.02344H12.3223L16 14.9766H13.416ZM13.3203 11.2646V13.1172H8.21387V11.2646H13.3203Z",
  ],
  DELISTED_BY_SELLER: [
    "M3.84575 6.09417C3.37704 5.63098 2.62296 5.63098 2.15425 6.09417L1.63232 6.60997C1.25735 6.98052 0.653522 6.97874 0.280752 6.60597C-0.0920188 6.2332 -0.093805 5.62937 0.276753 5.2544L0.792547 4.73247C1.25574 4.26376 1.25574 3.50968 0.792547 3.04097L0.276753 2.51904C-0.0938049 2.14407 -0.0920189 1.54024 0.280752 1.16747C0.653522 0.7947 1.25735 0.792914 1.63232 1.16347L2.15425 1.67927C2.62296 2.14245 3.37704 2.14245 3.84575 1.67927L4.36768 1.16347C4.74265 0.792914 5.34648 0.7947 5.71925 1.16747C6.09202 1.54024 6.09381 2.14407 5.72325 2.51904L5.20745 3.04097C4.74426 3.50968 4.74426 4.26376 5.20745 4.73247L5.72325 5.2544C6.0938 5.62937 6.09202 6.2332 5.71925 6.60597C5.34648 6.97874 4.74265 6.98052 4.36768 6.60997L3.84575 6.09417Z",
    "M13.6006 12.3311C13.6006 12.1761 13.5778 12.0371 13.5322 11.9141C13.4867 11.7865 13.4023 11.668 13.2793 11.5586C13.1562 11.4492 12.9785 11.3398 12.7461 11.2305C12.5182 11.1165 12.2174 10.998 11.8438 10.875C11.4062 10.7292 10.9847 10.5651 10.5791 10.3828C10.1735 10.196 9.8112 9.97949 9.49219 9.7334C9.17318 9.48275 8.92025 9.19108 8.7334 8.8584C8.54655 8.52116 8.45312 8.12923 8.45312 7.68262C8.45312 7.25423 8.54883 6.86914 8.74023 6.52734C8.93164 6.18099 9.19824 5.88704 9.54004 5.64551C9.88639 5.39941 10.292 5.21257 10.7568 5.08496C11.2217 4.9528 11.7298 4.88672 12.2812 4.88672C13.0104 4.88672 13.653 5.01432 14.209 5.26953C14.7695 5.52018 15.207 5.87793 15.5215 6.34277C15.8405 6.80306 16 7.34766 16 7.97656H13.6143C13.6143 7.72591 13.5618 7.50488 13.457 7.31348C13.3568 7.12207 13.2041 6.97168 12.999 6.8623C12.7939 6.75293 12.5365 6.69824 12.2266 6.69824C11.9258 6.69824 11.6729 6.74382 11.4678 6.83496C11.2627 6.92611 11.1077 7.04915 11.0029 7.2041C10.8981 7.35449 10.8457 7.52083 10.8457 7.70312C10.8457 7.85352 10.8867 7.99023 10.9688 8.11328C11.0553 8.23177 11.1761 8.34342 11.3311 8.44824C11.4906 8.55306 11.682 8.65332 11.9053 8.74902C12.1331 8.84473 12.3883 8.93815 12.6709 9.0293C13.1995 9.19792 13.6689 9.38704 14.0791 9.59668C14.4938 9.80176 14.8424 10.0365 15.125 10.3008C15.4121 10.5605 15.6286 10.8568 15.7744 11.1895C15.9248 11.5221 16 11.8981 16 12.3174C16 12.764 15.9134 13.1605 15.7402 13.5068C15.5671 13.8532 15.3187 14.1471 14.9951 14.3887C14.6715 14.6257 14.2842 14.8057 13.833 14.9287C13.3818 15.0518 12.8783 15.1133 12.3223 15.1133C11.8118 15.1133 11.3083 15.0495 10.8115 14.9219C10.3193 14.7897 9.87272 14.5892 9.47168 14.3203C9.07064 14.0469 8.74935 13.6982 8.50781 13.2744C8.27083 12.846 8.15234 12.3379 8.15234 11.75H10.5586C10.5586 12.0417 10.5973 12.2878 10.6748 12.4883C10.7523 12.6888 10.8662 12.8506 11.0166 12.9736C11.167 13.0921 11.3516 13.1787 11.5703 13.2334C11.7891 13.2835 12.0397 13.3086 12.3223 13.3086C12.6276 13.3086 12.8737 13.2653 13.0605 13.1787C13.2474 13.0876 13.3841 12.9691 13.4707 12.8232C13.5573 12.6729 13.6006 12.5088 13.6006 12.3311Z",
  ],
  APPROVED_FOR_PRODUCTION: [
    "M3.50283 6.32122C3.1123 6.71175 2.47914 6.71175 2.08861 6.32122L0.283887 4.5165C-0.0946289 4.13798 -0.0946292 3.52429 0.283887 3.14577C0.662403 2.76725 1.2761 2.76725 1.65461 3.14577L2.08861 3.57977C2.47914 3.97029 3.1123 3.97029 3.50283 3.57977L5.54243 1.54016C5.92095 1.16165 6.53464 1.16165 6.91316 1.54016C7.29168 1.91868 7.29168 2.53237 6.91316 2.91089L3.50283 6.32122Z",
    "M12.1765 14.3718C11.6672 14.8646 10.8424 14.8681 10.3286 14.3797C9.80822 13.8851 9.80451 13.075 10.3203 12.576L11.4866 11.4477H7.31415C6.58837 11.4477 6 10.8784 6 10.1763C6 9.4741 6.58837 8.90488 7.31415 8.90488H11.4866L10.3258 7.76409C9.82263 7.26953 9.82586 6.47491 10.3331 5.98421C10.8431 5.49075 11.6701 5.49075 12.1801 5.9842L15.6371 9.32868C16.121 9.79679 16.121 10.5557 15.6371 11.0239L12.1765 14.3718Z",
  ],
  APPROVED_FOR_BETA: [
    "M3.50283 6.32122C3.1123 6.71175 2.47914 6.71175 2.08861 6.32122L0.283887 4.5165C-0.0946289 4.13798 -0.0946292 3.52429 0.283887 3.14577C0.662403 2.76725 1.2761 2.76725 1.65461 3.14577L2.08861 3.57977C2.47914 3.97029 3.1123 3.97029 3.50283 3.57977L5.54243 1.54016C5.92095 1.16165 6.53464 1.16165 6.91316 1.54016C7.29168 1.91868 7.29168 2.53237 6.91316 2.91089L3.50283 6.32122Z",
    "M12.1765 14.3718C11.6672 14.8646 10.8424 14.8681 10.3286 14.3797C9.80822 13.8851 9.80451 13.075 10.3203 12.576L11.4866 11.4477H7.31415C6.58837 11.4477 6 10.8784 6 10.1763C6 9.4741 6.58837 8.90488 7.31415 8.90488H11.4866L10.3258 7.76409C9.82263 7.26953 9.82586 6.47491 10.3331 5.98421C10.8431 5.49075 11.6701 5.49075 12.1801 5.9842L15.6371 9.32868C16.121 9.79679 16.121 10.5557 15.6371 11.0239L12.1765 14.3718Z",
  ],
  SUBMITTED_FOR_PRODUCTION: [
    "M0.718749 5.71645C1.19792 6.19561 1.79167 6.4352 2.5 6.4352C3.20833 6.4352 3.80208 6.19561 4.28125 5.71645C4.76042 5.23728 5 4.64353 5 3.9352C5 3.22686 4.76042 2.63311 4.28125 2.15395C3.80208 1.67478 3.20833 1.4352 2.5 1.4352C1.79167 1.4352 1.19792 1.67478 0.718749 2.15395C0.239583 2.63311 0 3.22686 0 3.9352C0 4.64353 0.239583 5.23728 0.718749 5.71645Z",
    "M12.1765 14.1929C11.6672 14.6857 10.8424 14.6892 10.3286 14.2008C9.80822 13.7062 9.80451 12.8961 10.3203 12.397L11.4866 11.2687H7.31415C6.58837 11.2687 6 10.6995 6 9.99735C6 9.29518 6.58837 8.72596 7.31415 8.72596H11.4866L10.3258 7.58517C9.82263 7.09061 9.82586 6.29599 10.3331 5.80528C10.8431 5.31183 11.6701 5.31183 12.1801 5.80528L15.6371 9.14976C16.121 9.61787 16.121 10.3768 15.6371 10.8449L12.1765 14.1929Z",
  ],
  SUBMITTED_FOR_STAGING: [
    "M0.718749 5.71645C1.19792 6.19561 1.79167 6.4352 2.5 6.4352C3.20833 6.4352 3.80208 6.19561 4.28125 5.71645C4.76042 5.23728 5 4.64353 5 3.9352C5 3.22686 4.76042 2.63311 4.28125 2.15395C3.80208 1.67478 3.20833 1.4352 2.5 1.4352C1.79167 1.4352 1.19792 1.67478 0.718749 2.15395C0.239583 2.63311 0 3.22686 0 3.9352C0 4.64353 0.239583 5.23728 0.718749 5.71645Z",
    "M12.1765 14.1929C11.6672 14.6857 10.8424 14.6892 10.3286 14.2008C9.80822 13.7062 9.80451 12.8961 10.3203 12.397L11.4866 11.2687H7.31415C6.58837 11.2687 6 10.6995 6 9.99735C6 9.29518 6.58837 8.72596 7.31415 8.72596H11.4866L10.3258 7.58517C9.82263 7.09061 9.82586 6.29599 10.3331 5.80528C10.8431 5.31183 11.6701 5.31183 12.1801 5.80528L15.6371 9.14976C16.121 9.61787 16.121 10.3768 15.6371 10.8449L12.1765 14.1929Z",
  ],
  REJECTED_FOR_PRODUCTION: [
    "M3.84575 6.14265C3.37704 5.67946 2.62296 5.67946 2.15425 6.14265L1.63232 6.65844C1.25735 7.029 0.653522 7.02721 0.280752 6.65444C-0.0920188 6.28167 -0.093805 5.67785 0.276753 5.30288L0.792547 4.78094C1.25574 4.31224 1.25574 3.55815 0.792547 3.08945L0.276753 2.56751C-0.0938049 2.19254 -0.0920188 1.58872 0.280752 1.21595C0.653522 0.843176 1.25735 0.84139 1.63232 1.21195L2.15425 1.72774C2.62296 2.19093 3.37704 2.19093 3.84575 1.72774L4.36768 1.21195C4.74265 0.84139 5.34648 0.843177 5.71925 1.21595C6.09202 1.58872 6.0938 2.19254 5.72325 2.56751L5.20745 3.08945C4.74426 3.55815 4.74426 4.31224 5.20745 4.78094L5.72325 5.30288C6.0938 5.67785 6.09202 6.28167 5.71925 6.65444C5.34648 7.02721 4.74265 7.029 4.36768 6.65844L3.84575 6.14265Z",
    "M12.1765 14.6929C11.6672 15.1857 10.8424 15.1892 10.3286 14.7008C9.80822 14.2062 9.80451 13.3961 10.3203 12.897L11.4866 11.7687H7.31415C6.58837 11.7687 6 11.1995 6 10.4973C6 9.79518 6.58837 9.22596 7.31415 9.22596H11.4866L10.3258 8.08516C9.82263 7.59061 9.82586 6.79599 10.3331 6.30528C10.8431 5.81183 11.6701 5.81183 12.1801 6.30528L15.6371 9.64976C16.121 10.1179 16.121 10.8768 15.6371 11.3449L12.1765 14.6929Z",
  ],
  APPROVED_FOR_STAGING: [
    "M3.50283 6.32122C3.1123 6.71175 2.47914 6.71175 2.08861 6.32122L0.283887 4.5165C-0.0946289 4.13798 -0.0946292 3.52429 0.283887 3.14577C0.662403 2.76725 1.2761 2.76725 1.65461 3.14577L2.08861 3.57977C2.47914 3.97029 3.1123 3.97029 3.50283 3.57977L5.54243 1.54016C5.92095 1.16165 6.53464 1.16165 6.91316 1.54016C7.29168 1.91868 7.29168 2.53237 6.91316 2.91089L3.50283 6.32122Z",
    "M12.1765 14.3718C11.6672 14.8646 10.8424 14.8681 10.3286 14.3797C9.80822 13.8851 9.80451 13.075 10.3203 12.576L11.4866 11.4477H7.31415C6.58837 11.4477 6 10.8784 6 10.1763C6 9.4741 6.58837 8.90488 7.31415 8.90488H11.4866L10.3258 7.76409C9.82263 7.26953 9.82586 6.47491 10.3331 5.98421C10.8431 5.49075 11.6701 5.49075 12.1801 5.9842L15.6371 9.32868C16.121 9.79679 16.121 10.5557 15.6371 11.0239L12.1765 14.3718Z",
  ],
  DELISTED_BY_APPROVER: [
    "M3.84575 6.23089C3.37704 5.7677 2.62296 5.7677 2.15425 6.23089L1.63232 6.74668C1.25735 7.11724 0.653522 7.11546 0.280752 6.74269C-0.0920188 6.36992 -0.093805 5.76609 0.276753 5.39112L0.792547 4.86918C1.25574 4.40048 1.25574 3.64639 0.792547 3.17769L0.276753 2.65576C-0.0938049 2.28079 -0.0920189 1.67696 0.280752 1.30419C0.653522 0.931419 1.25735 0.929632 1.63232 1.30019L2.15425 1.81598C2.62296 2.27917 3.37704 2.27917 3.84575 1.81598L4.36768 1.30019C4.74265 0.929633 5.34648 0.931419 5.71925 1.30419C6.09202 1.67696 6.09381 2.28079 5.72325 2.65576L5.20745 3.17769C4.74426 3.64639 4.74426 4.40048 5.20745 4.86918L5.72325 5.39112C6.0938 5.76609 6.09202 6.36991 5.71925 6.74269C5.34648 7.11546 4.74265 7.11724 4.36768 6.74668L3.84575 6.23089Z",
    "M11.4062 7.11523L8.97949 14.9766H6.40234L10.0596 5.02344H11.6934L11.4062 7.11523ZM13.416 14.9766L10.9824 7.11523L10.668 5.02344H12.3223L16 14.9766H13.416ZM13.3203 11.2646V13.1172H8.21387V11.2646H13.3203Z",
  ],
};

const getStatusOrder = () => {
  if (
    sessionStorage.getItem("domain") === "NONE" &&
    getConfig().REACT_APP_PORTAL === "seller"
  ) {
    return [
      "DRAFT",
      "SUBMITTED_FOR_PRODUCTION",
      "IN_REVIEW",
      "APPROVED_FOR_PRODUCTION",
      "REJECTED_FOR_PRODUCTION",
      "PUBLISHED",
      "DELISTED_BY_ADMIN",
      "DELISTED_BY_SELLER",
    ];
  }

  return [
    "DRAFT",
    "SUBMITTED_FOR_STAGING",
    "SUBMITTED_FOR_PRODUCTION",
    "IN_REVIEW",
    "APPROVED_FOR_BETA",
    "APPROVED_FOR_STAGING",
    "APPROVED_FOR_PRODUCTION",
    "REJECTED_FOR_PRODUCTION",
    "PUBLISHED",
    "DELISTED_BY_SELLER",
    "DELISTED_BY_APPROVER",
  ];
};

const renderStatusIcon = (status, isSelected) => {
  const iconPaths = STATUS_ICON_PATHS[status];

  if (!iconPaths) {
    return null;
  }

  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {iconPaths.map((pathData) => (
        <path
          key={pathData}
          d={pathData}
          fill={isSelected ? "#0476E2" : "#121415"}
          fillOpacity="0.6"
        />
      ))}
    </svg>
  );
};

const getStatusTextClass = (status, isSelected) => {
  if (status === "DRAFT") {
    return isSelected
      ? styles.sideNavStatusClsSelected
      : styles.sideNavStatusCls;
  }

  return styles.sideNavStatusCls;
};

const getStatusTextSx = (status, isSelected) => {
  if (status === "DRAFT") {
    return undefined;
  }

  return {
    "&.MuiListItemText-root": {
      color: isSelected ? "#0476E2 !important" : "#2a2c2d !important",
    },
  };
};

const shouldUseCompactSidebar = () => {
  return (
    Boolean(sessionStorage.getItem("isOemSeller")) ||
    sessionStorage.getItem("idpLogin") === "true"
  );
};

export const Sidebar = ({ itemList, getItemBasedOnStatus, props: sidebarProps }) => {
  const [sideNavStatusData, setSideNavStatusData] = useState({});
  const [totalAppCount, setTotalAppCount] = useState(0);
  const [isDataReady, setIsDataReady] = useState(false);

  const [open, setOpen] = useState(true);

  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isOemActivitySelected, setIsOemActivitySelected] = useState(false);
  const handleClick = () => {
    setOpen(!open);
  };

  const handleListSelectedApps = (key, index) => {
    setSelectedIndex(index);
    setIsOemActivitySelected(false);
    getItemBasedOnStatus(key);
    sessionStorage.setItem("selectedAppStatus", key);
  };

  useEffect(() => {
    // Reset data ready state when itemList changes
    setIsDataReady(false);

    // Only calculate counts if we have valid array data
    if (!itemList || !Array.isArray(itemList) || itemList.length === 0) {
      setSideNavStatusData({});
      setTotalAppCount(0);
      return;
    }

    const statusObj = itemList.map((app) => app.statuses);
    const flattenedStatuses = statusObj.reduce((result, statuses) => {
      result.push(...statuses);
      return result;
    }, []);
    const statusOrder = getStatusOrder();

    flattenedStatuses.sort(function (a, b) {
      return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
    });

    const countsByStatus = {};
    flattenedStatuses.forEach(({ status }) => {
      countsByStatus[status] = (countsByStatus[status] || 0) + 1;
    });
    statusOrder.forEach((statusKey) => {
      if (countsByStatus[statusKey] === undefined) {
        countsByStatus[statusKey] = 0;
      }
    });

    const orderedStatusList = Object.fromEntries(
      statusOrder.map((key) => [key, countsByStatus[key] || 0])
    );
    setTotalAppCount(flattenedStatuses.length);
    if (Object.keys(orderedStatusList).length) {
      setSideNavStatusData(orderedStatusList);
      setIsDataReady(true);
    }

    // Restore selected index based on sessionStorage
    const selectedStatus = sessionStorage.getItem("selectedAppStatus");
    if (selectedStatus && selectedStatus !== "all") {
      const statusIndex = statusOrder.indexOf(selectedStatus);
      if (statusIndex !== -1) {
        setSelectedIndex(statusIndex);
      }
    } else {
      setSelectedIndex(null);
    }
  }, [itemList]);

  const compactSidebar = shouldUseCompactSidebar();

  const renderSidebarStatusItem = (key, index) => {
    if (key === "GENERATING_DELTA") {
      return null;
    }

    const itemCount = sideNavStatusData[key];
    const isSelected = selectedIndex === index;
    const isEnabled = itemCount > 0;

    return (
      <ListItem
        sx={{
          fontSize: "14px !important",
          fontWeight: "400",
          letterSpacing: "0.25px",
          color: "#717273",
          "& .MuiListItemSecondaryAction-root": {
            color: isEnabled && isSelected ? "#0476E2" : "#717273",
          },
          ...(compactSidebar && {
            "@media(max-height:600px)": {
              marginLeft: "-5px",
            },
          }),
        }}
        disablePadding
        key={key}
        disableGutters
        secondaryAction={itemCount === 0 ? "0" : itemCount}
      >
        <ListItemButton
          sx={{
            paddingRight: "0px !important",
            right: "-10px !important",
            "&:hover": {
              backgroundColor: "#E5F3FF !important",
              borderRadius: "4px",
            },
            cursor: isEnabled ? "pointer" : "default",
            "&.Mui-selected": {
              backgroundColor: "#E5F3FF !important",
              borderRadius: "4px !important",
            },
          }}
          selected={isSelected}
          onClick={() => {
            if (isEnabled) {
              handleListSelectedApps(key, index);
            }
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: "28px !important",
            }}
          >
            {renderStatusIcon(key, isSelected)}
          </ListItemIcon>
          <ListItemText
            className={getStatusTextClass(key, isSelected)}
            primary={STATUS_LABELS[key]}
            sx={getStatusTextSx(key, isSelected)}
            disableTypography
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    
      <List
        sx={{
          width: "19%",
          maxWidth: 360,
          bgcolor: "background.paper",
          position: "fixed",

          ...(compactSidebar && {
            "@media(max-height:600px)": {
              position: "absolute",
              overflowY: "scroll",
              overflowX: "hidden",
              height: "480px",
              width: "251px",
              "& .MuiButtonBase-root": {
                marginLeft: "-8px !important",
              },
            },
          }),
        }}
      >
        <ListItemButton
          disablePadding
          sx={{
            color: "#121415",
            fontWeight: "600",
            fontSize: "14px",
            width: "108%",
          }}
        >
          {open ? (
            <ArrowDropDownRoundedIcon onClick={handleClick} />
          ) : (
            <ArrowDropUpRoundedIcon onClick={handleClick} />
          )}
          <ListItem
            disableGutters
            secondaryAction={isDataReady ? totalAppCount : ""}
            onClick={(event) => handleListSelectedApps("all")}
          >
            <ListItemText
              disableTypography
              sx={{ fontSize: "14px", fontWeight: 600, color: "#121415" }}
              primary={sidebarProps?.type === "Feature" ? "All Features" : "All Apps"}
            />
          </ListItem>
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit>
          {isDataReady &&
            Object.keys(sideNavStatusData).map((key, index) =>
              renderSidebarStatusItem(key, index)
            )}
        </Collapse>
        {sidebarProps?.type !== "Feature" && (
          <div >
            <ListItem
              disablePadding
              disableGutters
              secondaryAction={
                isDataReady
                  ? (sideNavStatusData["PUBLISHED"] || 0) +
                    (sideNavStatusData["APPROVED_FOR_PRODUCTION"] || 0)
                  : ""
              }
              sx={{
                fontSize: "14px !important",
                fontWeight: "400",
                letterSpacing: "0.25px",
                color: "#717273",
                "& .MuiListItemSecondaryAction-root": {
                  color: isOemActivitySelected ? "#0476E2" : "#717273",
                },
              }}
            >
              <ListItemButton
                sx={{
                  paddingRight: "0px !important",
                  right: "-10px !important",
                  "&:hover": {
                    backgroundColor: "#E5F3FF !important",
                    borderRadius: "4px",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#E5F3FF !important",
                    borderRadius: "4px !important",
                  },
                }}
                selected={isOemActivitySelected}
                onClick={() => {
                  setSelectedIndex(null);
                  setIsOemActivitySelected(true);
                  getItemBasedOnStatus("OEM_ACTIVITY_STATUS");
                  sessionStorage.setItem("selectedAppStatus", "OEM_ACTIVITY_STATUS");
                }}
              >
                <ListItemIcon sx={{ minWidth: "28px !important" }}>
                  
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 8C8 6.89697 8.39091 5.95455 9.17273 5.17273C9.95455 4.39091 10.897 4 12 4C13.103 4 14.0455 4.39091 14.8273 5.17273C15.6091 5.95455 16 6.89697 16 8H8ZM4 12C2.89697 12 1.95455 11.6091 1.17273 10.8273C0.390909 10.0455 0 9.10303 0 8H8C8 9.10303 7.60909 10.0455 6.82727 10.8273C6.04545 11.6091 5.10303 12 4 12ZM8 8C6.89697 8 5.95455 7.60909 5.17273 6.82727C4.39091 6.04545 4 5.10303 4 4C4 2.89697 4.39091 1.95455 5.17273 1.17273C5.95455 0.390909 6.89697 0 8 0V8ZM8 16V8C9.10303 8 10.0455 8.39091 10.8273 9.17273C11.6091 9.95455 12 10.897 12 12C12 13.103 11.6091 14.0455 10.8273 14.8273C10.0455 15.6091 9.10303 16 8 16Z" 
                      fill={isOemActivitySelected ? "#0476E2" : "#12141599"}/>
                    </svg>
                 
                </ListItemIcon>
                <ListItemText
                  primary="OEM Activity Status"
                  disableTypography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 400,
                    color: isOemActivitySelected ? "#0476E2" : "#121415",
                  }}
                />
              </ListItemButton>
            </ListItem>
          </div>
        )}

      </List>
    
  );
};

Sidebar.propTypes = {
  itemList: PropTypes.arrayOf(
    PropTypes.shape({
      statuses: PropTypes.arrayOf(
        PropTypes.shape({
          status: PropTypes.string,
        })
      ),
    })
  ),
  getItemBasedOnStatus: PropTypes.func,
  props: PropTypes.shape({
    type: PropTypes.string,
  }),
};
